import React from 'react';
import { VALID_RELATIONSHIP_TYPES } from '../constants.js';

/**
 * LinkEditor - Sub-form for adding Event_Links with target event search/select,
 * relationship type dropdown, and optional label.
 *
 * Props:
 * - links: array of { targetId, relationship, label } objects
 * - onChange(links): callback when links change
 * - existingEvents: array of { id, title } for target selection
 */
function LinkEditor({ links = [], onChange, existingEvents = [] }) {
  function handleAddLink() {
    onChange([...links, { targetId: '', relationship: '', label: '' }]);
  }

  function handleRemoveLink(index) {
    const updated = links.filter((_, i) => i !== index);
    onChange(updated);
  }

  function handleLinkChange(index, field, value) {
    const updated = links.map((link, i) => {
      if (i !== index) return link;
      return { ...link, [field]: value };
    });
    onChange(updated);
  }

  function handleRelationshipChange(index, value) {
    if (value === '__custom__') {
      handleLinkChange(index, 'relationship', '');
      handleLinkChange(index, '_useCustomRelationship', true);
    } else {
      const updated = links.map((link, i) => {
        if (i !== index) return link;
        return { ...link, relationship: value, _useCustomRelationship: false };
      });
      onChange(updated);
    }
  }

  function isCustomRelationship(link) {
    if (link._useCustomRelationship) return true;
    if (!link.relationship) return false;
    return !VALID_RELATIONSHIP_TYPES.includes(link.relationship);
  }

  return (
    <div className="link-editor">
      {links.length === 0 && (
        <p className="link-editor-empty">No event links added yet.</p>
      )}

      {links.map((link, index) => (
        <div key={index} className="link-editor-entry" data-testid={`link-entry-${index}`}>
          <div className="link-editor-field">
            <label htmlFor={`link-target-${index}`}>Target Event</label>
            {existingEvents.length > 0 ? (
              <select
                id={`link-target-${index}`}
                value={link.targetId}
                onChange={(e) => handleLinkChange(index, 'targetId', e.target.value)}
              >
                <option value="">-- Select target event --</option>
                {existingEvents.map(evt => (
                  <option key={evt.id} value={evt.id}>{evt.title}</option>
                ))}
              </select>
            ) : (
              <input
                id={`link-target-${index}`}
                type="text"
                value={link.targetId}
                onChange={(e) => handleLinkChange(index, 'targetId', e.target.value)}
                placeholder="Enter event ID"
              />
            )}
          </div>

          <div className="link-editor-field">
            <label htmlFor={`link-relationship-${index}`}>Relationship</label>
            {isCustomRelationship(link) ? (
              <input
                id={`link-relationship-${index}`}
                type="text"
                value={link.relationship}
                onChange={(e) => handleLinkChange(index, 'relationship', e.target.value)}
                placeholder="Custom relationship type"
              />
            ) : (
              <select
                id={`link-relationship-${index}`}
                value={link.relationship}
                onChange={(e) => handleRelationshipChange(index, e.target.value)}
              >
                <option value="">-- Select relationship --</option>
                {VALID_RELATIONSHIP_TYPES.map(type => (
                  <option key={type} value={type}>{type.replace(/_/g, ' ')}</option>
                ))}
                <option value="__custom__">Custom...</option>
              </select>
            )}
          </div>

          <div className="link-editor-field">
            <label htmlFor={`link-label-${index}`}>Label (optional)</label>
            <input
              id={`link-label-${index}`}
              type="text"
              value={link.label || ''}
              onChange={(e) => handleLinkChange(index, 'label', e.target.value)}
              placeholder="Optional description"
            />
          </div>

          <button
            type="button"
            className="link-editor-remove-btn"
            onClick={() => handleRemoveLink(index)}
            aria-label={`Remove link ${index + 1}`}
          >
            Remove
          </button>
        </div>
      ))}

      <button
        type="button"
        className="link-editor-add-btn"
        onClick={handleAddLink}
      >
        Add Link
      </button>
    </div>
  );
}

export default LinkEditor;
